import * as Icons from 'lucide-react';
import { type ComponentType } from 'react';
import { getActionBody, getPrimaryQuickActions, type QuickAction } from '@court-copilot/prompts';
import { Button, Card } from '@/components/ui';
import { CustomPromptInput } from '@/components/CustomPromptInput';
import { LiveSuggestions } from '@/components/LiveSuggestions';
import { MoreActionsMenu } from '@/components/MoreActionsMenu';
import { SessionFeed } from '@/components/SessionFeed';
import { useHearingSessionContext } from '@/contexts/HearingSessionContext';
import { useSession } from '@/contexts/SessionContext';
import { useTranscriptionContext } from '@/contexts/TranscriptionContext';

const ACTIONS = getPrimaryQuickActions();

export function ResponsePanel() {
  const { status, error: sessionError, sessionId } = useSession();
  const { busy, ready, ask } = useHearingSessionContext();
  const { transcript } = useTranscriptionContext();
  const disabled = !ready;

  return (
    <div className="flex h-full flex-col gap-3">
      <Card variant="panel" padding="md">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Quick actions</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-600">
            {sessionStatusLabel(status, sessionError)}
          </p>
        </div>

        {/* Live skill triggering (P3 Section 4): glowing chips from the 30s
            transcript classifier, above the fixed quick-action rows. */}
        <LiveSuggestions
          sessionId={sessionId}
          transcript={transcript}
          disabled={disabled || busy}
          onRun={(prompt, label, mode) => void ask(prompt, label, mode)}
        />

        {/* Primary row: FAST tactical actions. Amber accent, hotkeys F1-F4. */}
        <div className="grid grid-cols-2 gap-2">
          {ACTIONS.map((action) => (
            <FastActionButton
              key={action.id}
              action={action}
              disabled={disabled || busy}
              onClick={() => void ask(getActionBody(action, 'fast'), action.id, 'fast')}
            />
          ))}
        </div>

        {/* Secondary row: DEEP research variants. Muted, hotkeys Shift+F1-F4. */}
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[9px] uppercase tracking-[0.2em] text-stone-600">Deep</span>
          <div className="h-px flex-1 bg-stone-800/70" />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {ACTIONS.map((action) => (
            <DeepActionButton
              key={action.id}
              action={action}
              disabled={disabled || busy}
              onClick={() => void ask(getActionBody(action, 'deep'), `deep-${action.id}`, 'deep')}
            />
          ))}
        </div>

        <div className="mt-3">
          <MoreActionsMenu
            disabled={disabled || busy}
            onSelect={(action) => void ask(getActionBody(action, 'deep'), action.id, 'deep')}
          />
        </div>
      </Card>

      <Card variant="panel" padding="md">
        <p className="mb-3 text-[10px] uppercase tracking-[0.2em] text-stone-500">Custom prompt</p>
        <CustomPromptInput
          disabled={disabled || busy}
          onSubmit={(prompt) => void ask(prompt, null, 'deep')}
        />
      </Card>

      <SessionFeed />
    </div>
  );
}

interface ActionButtonProps {
  action: QuickAction;
  disabled: boolean;
  onClick: () => void;
}

// Primary FAST button: amber quick-action treatment, F1-F4 hotkey, "~2s" hint.
function FastActionButton({ action, disabled, onClick }: ActionButtonProps) {
  const Icon = resolveIcon(action.icon);
  return (
    <Button
      variant="quickAction"
      size="sm"
      className="justify-start gap-3 px-3 py-2.5"
      title="Fast tactical (~2s)"
      leftIcon={Icon ? <Icon className="h-4 w-4" /> : null}
      rightSlot={
        action.hotkey ? (
          <kbd className="rounded bg-stone-800 px-1.5 py-0.5 text-[10px] text-stone-500">
            {action.hotkey}
          </kbd>
        ) : null
      }
      disabled={disabled}
      onClick={onClick}
    >
      <span className="text-sm">{action.label}</span>
    </Button>
  );
}

// Secondary DEEP button: lighter/smaller, muted stone-700 border, DEEP chip,
// Shift+Fn hotkey, "~30s" hint. Visually subordinate to the fast row.
function DeepActionButton({ action, disabled, onClick }: ActionButtonProps) {
  const Icon = resolveIcon(action.icon);
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title="Deep research with documents (~30s)"
      className="inline-flex items-center gap-2 rounded-md border border-stone-700/70 bg-transparent px-2.5 py-1.5 text-left text-xs text-stone-400 transition-colors hover:border-stone-600 hover:text-stone-200 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-stone-500" /> : null}
      <span className="truncate">{action.label}</span>
      <span className="ml-auto flex shrink-0 items-center gap-1">
        <span className="rounded border border-stone-700 px-1 py-0.5 text-[9px] tracking-[0.15em] text-stone-500">
          DEEP
        </span>
        {action.hotkey ? (
          <kbd className="rounded bg-stone-800/70 px-1 py-0.5 text-[9px] text-stone-600">
            ⇧{action.hotkey}
          </kbd>
        ) : null}
      </span>
    </button>
  );
}

function sessionStatusLabel(
  status: ReturnType<typeof useSession>['status'],
  error: string | null,
): string {
  if (status === 'idle') {
    return 'Select a matter';
  }
  if (status === 'loading') {
    return 'Starting session';
  }
  if (status === 'error') {
    return error ? 'Session error' : 'Session error';
  }
  return 'Session ready';
}

// The prompts package exports lucide icon names as strings so the api can
// reference them too. Resolve to actual components on the client.
type IconComponent = ComponentType<{ className?: string }>;

function resolveIcon(name: string): IconComponent | null {
  const all = Icons as unknown as Record<string, unknown>;
  const candidate = all[name];
  if (typeof candidate === 'function' || typeof candidate === 'object') {
    return candidate as IconComponent;
  }
  return null;
}
